import React, { useState, useRef, useEffect } from 'react'
import {ChevronDown, Globe} from 'lucide-react'
import { useTranslation } from '@/i18n/useTranslation'
import { localeList, Locale } from '@/i18n/translations'

export default function LanguagePicker() {
  const { locale, setLocale, t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const current = localeList.find(l => l.locale === locale) ?? localeList[0]

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white bg-opacity-10 hover:bg-opacity-20 text-white text-sm font-medium transition-all"
        aria-label={t.nav.language}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Globe size={16} aria-hidden="true" />
        <span>{current.flag}</span>
        <span className="hidden sm:inline">{current.name}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t.nav.language}
          className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-[9999]"
        >
          {localeList.map(item => (
            <li key={item.locale} role="option" aria-selected={item.locale === locale}>
              <button
                type="button"
                onClick={() => { setLocale(item.locale as Locale); setOpen(false) }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                  item.locale === locale
                    ? 'bg-blue-50 text-blue-800 font-semibold'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-lg">{item.flag}</span>
                <span>{item.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
